import React from "react";
import { ExternalLink, Globe } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Checkbox, FieldGroup, FormField, Input, NativeSelect, Textarea,
} from "../../ui";
import ImageField from "./ImageField";
import ListEditor from "./ListEditor";
import { EVENT_TYPE_OPTIONS, slugify } from "./eventForm";

const SUMMARY_MAX = 280;

/**
 * EventPageSection — how this webinar appears on the institute's website:
 * whether it is listed at /events, its type, address, one-line promise, cover,
 * what attendees will learn and who it is for.
 *
 * Speakers, agenda and FAQ are EventProgramSection — this card is the part a
 * visitor reads before deciding to scroll.
 */
const EventPageSection = ({ form, update, previews, setPreview, errors = {}, websiteUrl, title }) => {
  const w = form.website;
  const setWebsite = (patch) => update({ website: { ...w, ...patch } });
  const coverPreview = (w.coverImageKey && previews[w.coverImageKey]) || form.branding?.coverImageUrl || "";

  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <AppCardTitle>Event page on your website</AppCardTitle>
          <AppCardDescription>
            A landing page at /events with the registration form built in — the page to share in ads, posts and WhatsApp.
          </AppCardDescription>
        </div>
        {websiteUrl && (
          <AppButton as="a" href={websiteUrl} target="_blank" rel="noopener noreferrer" size="sm" variant="outline">
            <ExternalLink size={16} /> View page
          </AppButton>
        )}
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        <Checkbox
          checked={!!w.listed}
          onChange={(e) => setWebsite({ listed: e.target.checked })}
          label="Show this on my website's Events page"
          description="Listed with your other events, with its own page and registration form. Untick to keep it off the website — the registration link below still works."
        />

        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
          <FormField label="Event type" hint="The label visitors see. Every type runs as a live online session.">
            <NativeSelect
              value={form.eventType}
              options={EVENT_TYPE_OPTIONS}
              onChange={(e) => update({ eventType: e.target.value })}
            />
          </FormField>
          <FormField
            label="Page address"
            error={errors.slug}
            hint={`Visitors open it at /events/${w.slug || slugify(title) || "your-event"}`}
          >
            <Input
              value={w.slug}
              leading={<Globe size={14} />}
              placeholder={slugify(title) || "careers-in-data-science"}
              maxLength={80}
              onChange={(e) => setWebsite({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-{2,}/g, "-") })}
            />
          </FormField>
        </div>

        <FormField
          label="One-line summary"
          hint={`${(w.summary || "").length}/${SUMMARY_MAX} — shown on the event card, under the title, and in Google results.`}
        >
          <Textarea
            rows={2}
            value={w.summary}
            maxLength={SUMMARY_MAX}
            placeholder="A practical 90-minute roadmap to your first analyst job, with a live portfolio teardown."
            onChange={(e) => setWebsite({ summary: e.target.value })}
          />
        </FormField>

        <FieldGroup legend="Cover image" hint="16:9 works best, e.g. 1600 × 900. Shown on the event card and at the top of the page.">
          <ImageField
            previewUrl={coverPreview}
            linkUrl={w.coverImageKey ? "" : (form.branding?.coverImageUrl || "")}
            onUploaded={({ key, url }) => {
              setPreview(key, url);
              setWebsite({ coverImageKey: key });
            }}
            onLink={(url) => update({ website: { ...w, coverImageKey: "" }, branding: { ...form.branding, coverImageUrl: url } })}
            onClear={() => update({ website: { ...w, coverImageKey: "" }, branding: { ...form.branding, coverImageUrl: "" } })}
            label="Upload cover"
          />
        </FieldGroup>

        <FieldGroup legend="What attendees will learn" hint="Short outcomes, one per line. Press Enter to add the next.">
          <ListEditor
            items={w.highlights}
            onChange={(highlights) => setWebsite({ highlights })}
            itemLabel="Outcome"
            addLabel="Add an outcome"
            placeholder="e.g. The 5 skills every analyst job description asks for"
            max={12}
          />
        </FieldGroup>

        <FieldGroup legend="Who should attend" hint="Helps the right people recognise themselves.">
          <ListEditor
            items={w.audience}
            onChange={(audience) => setWebsite({ audience })}
            itemLabel="Audience"
            addLabel="Add an audience"
            placeholder="e.g. Final-year students"
            max={8}
          />
        </FieldGroup>
      </AppCardContent>
    </AppCard>
  );
};

export default EventPageSection;
